hour_slot<template>
  <div class="hourly-revenue-container">
    <!-- 查询条件 -->
    <el-card class="query-card" shadow="hover">
      <div slot="header" class="card-header">
        <i class="el-icon-search"></i>
        <span>查询条件</span>
      </div>
      <el-form :inline="true" :model="queryParams" size="small">
        <el-form-item label="广告主">
          <el-select
              clearable
              allow-create
            v-model="queryParams.advertiserId"
            placeholder="请选择广告主"
            filterable
            style="width: 240px"
          >
            <el-option
              v-for="item in advertiserList"
              :key="item.advertiser_id"
              :label="`${item.advertiser_name.replace('-SUNNY-SINO-TT', '')} (${item.advertiser_id})`"
              :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="广告组ID">
          <el-input 
            v-model="queryParams.adgroupId" 
            placeholder="请输入广告组ID（可选）"
            style="width: 200px"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="开始日期">
          <el-date-picker
            v-model="queryParams.startTime"
            type="date"
            placeholder="选择开始日期"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            style="width: 160px"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker
            v-model="queryParams.endTime"
            type="date"
            placeholder="选择结束日期"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            style="width: 160px"
          ></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 小时数据列表 -->
    <el-card class="list-card" shadow="hover" v-if="!loading && hourlyList.length > 0">
      <div slot="header" class="card-header">
        <i class="el-icon-tickets"></i>
        <span>小时数据列表</span>
        <div style="float: right">
          <el-button 
            size="mini" 
            type="warning" 
            icon="el-icon-data-analysis"
            :disabled="selectedRows.length < 2"
            @click="handleCompare"
          >趋势对比 ({{ selectedRows.length }})</el-button>
          <el-button 
            size="mini" 
            type="success" 
            icon="el-icon-download"
            @click="handleExport"
          >导出</el-button>
        </div>
      </div>
      <el-table 
        :data="hourlyList" 
        style="width: 100%"
        v-loading="tableLoading"
        highlight-current-row
        :height="tableHeight"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column prop="hour_slot" label="时间段" width="160" >
          <template slot-scope="scope">
            {{ formatTime(scope.row.hour_slot) }}
          </template>
        </el-table-column>
        <el-table-column prop="adgroup_id" label="广告组ID" width="150"></el-table-column>
        <el-table-column prop="adgroup_name" label="广告组名称" width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="spend" label="消耗" width="100" align="right" >
          <template slot-scope="scope">
            <span class="amount">{{ formatMoney(scope.row.spend) }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="conversions" label="转化数" width="100"></el-table-column>
        <!-- 动态生成1-24小时ROI列 -->
        <el-table-column v-for="h in 24" :key="h" :prop="'roas_h' + h" :label="h + '小时ROI'" min-width="111" align="right">
          <template slot-scope="scope">
            <span :class="getRoiClass(scope.row['roas_h' + h])">
              {{ (scope.row['roas_h' + h] || 0) === 0 ? '-' : (scope.row['roas_h' + h] || 0).toFixed(2) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" >
          <template slot-scope="scope">
            <el-button 
              size="mini" 
              type="text" 
              icon="el-icon-view"
              @click="handleViewDetail(scope.row)"
            >详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[50, 100, 200, 500]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        style="margin-top: 20px; text-align: right"
      ></el-pagination>
    </el-card>

    <!-- 空状态 -->
    <el-empty 
      v-if="!loading && hourlyList.length === 0" 
      description="暂无数据"
      style="margin-top: 40px"
    ></el-empty>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper">
      <i class="el-icon-loading"></i>
      <p>正在加载数据...</p>
    </div>

    <!-- 详情对话框 -->
    <el-dialog
      title="小时回收详情"
      :visible.sync="detailVisible"
      width="80%"
      append-to-body
    >
      <div v-if="detailData" class="detail-content">
        <!-- 基本信息 -->
        <el-descriptions :column="3" border>
          <el-descriptions-item label="广告主ID">{{ detailData.advertiser_id }}</el-descriptions-item>
          <el-descriptions-item label="广告组ID">{{ detailData.adgroup_id }}</el-descriptions-item>
          <el-descriptions-item label="时间段">{{ formatTime(detailData.hour_slot) }}</el-descriptions-item>
          <el-descriptions-item label="总消耗">
            <span class="amount">{{ formatMoney(detailData.spend) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="总展示">
            <span class="amount">{{ detailData.impressions || 0 }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="总点击">
            <span class="amount">{{ detailData.clicks || 0 }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="总转化">
            <span class="amount">{{ detailData.conversions || 0 }}</span>
          </el-descriptions-item>
        </el-descriptions>

        <!-- 回收趋势图 -->
        <div class="chart-section">
          <h3><i class="el-icon-data-line"></i> 回收趋势</h3>
          <div ref="trendChart" style="height: 400px;"></div>
        </div>

        <!-- 详细数据表格 -->
        <div class="table-section">
          <h3><i class="el-icon-s-grid"></i> 详细数据</h3>
          <el-table :data="detailData.revenue_timeline" border stripe max-height="400">
            <el-table-column prop="hour" label="小时后" width="100" align="center"></el-table-column>
            <el-table-column prop="revenue" label="回收金额" align="right">
              <template slot-scope="scope">
                <span class="amount">{{ formatMoney(scope.row.revenue) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="roas" label="ROI" align="right">
              <template slot-scope="scope">
                <span :class="getRoiClass(scope.row.roas)">
                  {{ (scope.row.roas || 0) === 0 ? '-' : (scope.row.roas || 0).toFixed(2) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>

    <!-- 趋势对比对话框 -->
    <el-dialog
      title="回收趋势对比"
      :visible.sync="compareVisible"
      width="90%"
      append-to-body
    >
      <div v-if="compareData && compareData.length > 0" class="compare-content">
        <el-alert
          :title="`已选择 ${compareData.length} 个时间段进行对比`"
          type="info"
          :closable="false"
          style="margin-bottom: 20px"
        ></el-alert>
        
        <!-- 对比图表 -->
        <div class="chart-section">
          <h3><i class="el-icon-data-analysis"></i> ROI 趋势对比</h3>
          <div ref="compareChart" style="height: 500px;"></div>
        </div>

        <!-- 对比数据表格 -->
        <div class="table-section">
          <h3><i class="el-icon-s-grid"></i> 详细对比数据</h3>
          <el-table :data="compareTableData" border stripe max-height="400">
            <el-table-column prop="hourOffset" label="小时后" width="100" align="center" fixed></el-table-column>
            <el-table-column 
              v-for="(item, index) in compareData" 
              :key="index"
              :label="formatTime(item.hour_slot)"
              align="right"
              min-width="120"
            >
              <template slot-scope="scope">
                <span :class="getRoiClass(scope.row['roi_' + index])">
                  {{ (scope.row['roi_' + index] || 0) === 0 ? '-' : (scope.row['roi_' + index] || 0).toFixed(2) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { advertiserApi } from '@/api/advertiserApi';

export default {
  name: 'HourlyRevenueMonitor',
  data() {
    return {
      loading: false,
      tableLoading: false,
      queryParams: {
        advertiserId: '',
        adgroupId: '',
        startTime: '',
        endTime: ''
      },
      hourlyList: [],
      pagination: {
        page: 1,
        pageSize: 100,
        total: 0
      },
      detailVisible: false,
      detailData: null,
      trendChart: null,
      // 广告主列表
      advertiserList: [],
      // 选中的行
      selectedRows: [],
      // 对比相关
      compareVisible: false,
      compareData: [],
      compareChart: null,
      // 表格高度
      tableHeight: 600
    };
  },
  mounted() {
    // 设置默认时间范围（最近7天）
    this.setDefaultTimeRange();
    // 获取广告主列表
    this.fetchAdvertisers();
    // 计算表格高度
    this.calculateTableHeight();
    // 监听窗口大小变化
    window.addEventListener('resize', this.calculateTableHeight);
  },
  computed: {
    // 对比表格数据
    compareTableData() {
      if (!this.compareData || this.compareData.length === 0) return [];
      
      // 找出最大的小时后数
      let maxHour = 0;
      this.compareData.forEach(item => {
        if (item.revenue_timeline && item.revenue_timeline.length > maxHour) {
          maxHour = item.revenue_timeline.length;
        }
      });
      
      // 构建表格数据
      const tableData = [];
      for (let h = 1; h <= maxHour; h++) {
        const row = { hourOffset: h };
        this.compareData.forEach((item, index) => {
          const detail = item.revenue_timeline?.find(d => d.hour === h);
          row['roi_' + index] = detail ? detail.roas : 0;
        });
        tableData.push(row);
      }
      
      return tableData;
    }
  },
  methods: {
    setDefaultTimeRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      
      this.queryParams.endTime = this.formatDate(end);
      this.queryParams.startTime = this.formatDate(start);
    },
    
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    async handleQuery() {
      // if (!this.queryParams.advertiserId) {
      //   this.$message.warning('请选择广告主');
      //   return;
      // }
      
      this.loading = true;
      this.tableLoading = true;
      
      try {
        const params = {
          advertiserId: this.queryParams.advertiserId,
          startTime: this.queryParams.startTime ? this.queryParams.startTime + ' 00:00:00' : '',
          endTime: this.queryParams.endTime ? this.queryParams.endTime + ' 23:59:59' : '',
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };
        
        if (this.queryParams.adgroupId) {
          params.adgroupId = this.queryParams.adgroupId;
        }
        
        const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/list', {withCredentials:false, params });
        
        if (response.data.code === 200) {
          this.hourlyList = response.data.data.list || [];
          this.pagination.total = response.data.data.total || 0;
          this.$message.success(`查询成功，共 ${this.pagination.total} 条数据`);
        } else {
          this.$message.error(response.data.message || '查询失败');
        }
      } catch (error) {
        console.error('查询失败:', error);
        this.$message.error('查询失败: ' + (error.message || '网络错误'));
      } finally {
        this.loading = false;
        this.tableLoading = false;
      }
    },
    
    handleReset() {
      this.queryParams = {
        advertiserId: '',
        adgroupId: '',
        startTime: '',
        endTime: ''
      };
      this.setDefaultTimeRange();
      this.hourlyList = [];
      this.pagination = {
        page: 1,
        pageSize: 100,
        total: 0
      };
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.page = 1;
      this.handleQuery();
    },
    
    handleCurrentChange(val) {
      this.pagination.page = val;
      this.handleQuery();
    },
    
    async handleViewDetail(row) {
      try {
        const response = await this.$http.get('https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/track', {
          withCredentials: false,
          params: {
            advertiserId: row.advertiser_id,
            adgroupId: row.adgroup_id,
            hourSlot: this.convertTimeForApi(row.hour_slot)
          }
        });
        
        if (response.data.code === 200) {
          this.detailData = response.data.data;
          this.detailVisible = true;
          
          // 延迟初始化图表，等待DOM渲染
          this.$nextTick(() => {
            this.initTrendChart();
          });
        } else {
          this.$message.error(response.data.message || '查询详情失败');
        }
      } catch (error) {
        console.error('查询详情失败:', error);
        this.$message.error('查询详情失败: ' + (error.message || '网络错误'));
      }
    },
    
    initTrendChart() {
      if (!this.$refs.trendChart) return;
      
      if (this.trendChart) {
        this.trendChart.dispose();
      }
      
      this.trendChart = echarts.init(this.$refs.trendChart);
      
      const hours = this.detailData.revenue_timeline.map(item => `${item.hour}h`);
      const revenues = this.detailData.revenue_timeline.map(item => item.revenue);
      
      const option = {
        title: {
          text: '回收趋势分析',
          left: 'right'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: function(params) {
            let result = params[0].name + '<br/>';
            params.forEach(item => {
              result += item.marker + item.seriesName + ': ' + 
                       (item.value === 0 ? '-' : '$' + item.value.toFixed(2)) + '<br/>';
            });
            return result;
          }
        },
        legend: {
          data: ['单小时回收'],
          top: 30
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: 70,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: hours,
          boundaryGap: false,
          name: '小时后'
        },
        yAxis: {
          type: 'value',
          name: '金额',
          axisLabel: {
            formatter: '${value}'
          }
        },
        series: [
          {
            name: '单小时回收',
            type: 'line',
            data: revenues,
            smooth: true,
            itemStyle: {
              color: '#409EFF'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
              ])
            }
          }
        ]
      };
      
      this.trendChart.setOption(option);
    },
    
    formatMoney(value) {
      if (!value && value !== 0) return '$0.00';
      return `$${Number(value).toFixed(2)}`;
    },
    
    formatTime(timeStr) {
      if (!timeStr) return '-';
      // 处理 ISO 格式: "2026-04-27T03:00" -> "2026-04-27 03:00:00"
      try {
        const date = new Date(timeStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        return timeStr;
      }
    },
    
    // 简化时间格式用于图例：MM-dd HH:mm
    formatShortTime(timeStr) {
      if (!timeStr) return '-';
      try {
        const date = new Date(timeStr);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${month}-${day} ${hours}:${minutes}`;
      } catch (e) {
        return timeStr;
      }
    },
    
    // 将时间转换为后端需要的格式
    convertTimeForApi(timeStr) {
      if (!timeStr) return '';
      // 如果已经是标准格式，直接返回
      if (timeStr.includes(' ') && timeStr.split(' ')[1].includes(':')) {
        // 确保有秒数
        const parts = timeStr.split(' ');
        if (parts[1].split(':').length === 2) {
          return `${parts[0]} ${parts[1]}:00`;
        }
        return timeStr;
      }
      // 处理 ISO 格式
      return this.formatTime(timeStr);
    },
    
    getRoiClass(roi) {
      if (!roi) return '';
      if (roi >= 1.5) return 'roi-excellent';
      if (roi >= 1.0) return 'roi-good';
      if (roi >= 0.5) return 'roi-warning';
      return 'roi-danger';
    },
    
    handleExport() {
      this.$message.info('导出功能开发中...');
    },
    
    // 计算表格高度
    calculateTableHeight() {
      this.$nextTick(() => {
        // 获取窗口高度
        const windowHeight = window.innerHeight;
        // 减去其他元素的高度：顶部导航(60) + 查询卡片(约120) + 卡片头部(约60) + 分页(约60) + 上下padding(40)
        const otherHeight = 400;
        this.tableHeight = windowHeight - otherHeight;
        // 设置最小高度
        if (this.tableHeight < 300) {
          this.tableHeight = 260;
        }
      });
    },
    
    // 获取广告主列表
    async fetchAdvertisers() {
      try {
        const res = await advertiserApi.getList({
          page: 1,
          pageSize: 100
        });
        if (res.code === 0) {
          this.advertiserList = res.data.list || [];
          // 如果有广告主且当前未选择，自动选择第一个
          if (this.advertiserList.length > 0 && !this.queryParams.advertiserId) {
            this.queryParams.advertiserId = this.advertiserList[0].advertiser_id;
          }
        }
      } catch (error) {
        console.error('获取广告主列表失败', error);
        this.$message.error('获取广告主列表失败');
      }
    },
    
    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    
    // 趋势对比
    async handleCompare() {
      if (this.selectedRows.length < 2) {
        this.$message.warning('请至少选择2个时间段进行对比');
        return;
      }
      
      if (this.selectedRows.length > 5) {
        this.$message.warning('最多只能对比5个时间段');
        return;
      }
      
      try {
        this.tableLoading = true;
        
        // 构建时间段列表
        const hourSlots = this.selectedRows
          .map(row => this.convertTimeForApi(row.hour_slot))
          .join(',');
        
        const response = await this.$http.get(
          'https://tiktoktf.darknight.games/api/api/v1/adgroup/hourly-revenue/trend-compare',
          {
            withCredentials: false,
            params: {
              advertiserId: this.queryParams.advertiserId,
              adgroupId: this.selectedRows[0].adgroup_id,
              hourSlots: hourSlots
            }
          }
        );
        
        if (response.data.code === 200) {
          this.compareData = response.data.data.comparisons || [];
          this.compareVisible = true;
          
          // 延迟初始化对比图表
          this.$nextTick(() => {
            this.initCompareChart();
          });
        } else {
          this.$message.error(response.data.message || '对比失败');
        }
      } catch (error) {
        console.error('对比失败:', error);
        this.$message.error('对比失败: ' + (error.message || '网络错误'));
      } finally {
        this.tableLoading = false;
      }
    },
    
    // 初始化对比图表
    initCompareChart() {
      if (!this.$refs.compareChart || this.compareData.length === 0) return;
          
      if (this.compareChart) {
        this.compareChart.dispose();
      }
          
      this.compareChart = echarts.init(this.$refs.compareChart);
          
      // 找出最大的小时后数
      let maxHour = 0;
      this.compareData.forEach(item => {
        if (item.revenue_timeline && item.revenue_timeline.length > maxHour) {
          maxHour = item.revenue_timeline.length;
        }
      });
          
      const hours = Array.from({ length: maxHour }, (_, i) => `${i + 1}h`);
          
      // 生成多条折线，使用简化的时间格式
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399'];
      const legendNames = [];
      const series = this.compareData.map((item, index) => {
        const data = [];
        for (let h = 1; h <= maxHour; h++) {
          const detail = item.revenue_timeline?.find(d => d.hour === h);
          data.push(detail ? detail.roas : 0);
        }
            
        // 简化的时间格式：04-27 03:00
        const legendName = this.formatShortTime(item.hour_slot);
        legendNames.push(legendName);
            
        return {
          name: legendName,
          type: 'line',
          data: data,
          smooth: true,
          itemStyle: {
            color: colors[index % colors.length]
          },
          lineStyle: {
            width: 2
          }
        };
      });
          
      const option = {
        title: {
          text: '多时间段 ROI 对比',
          left: 'center',
          top: 10
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: legendNames,
          top: 45,
          type: 'scroll',
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: 90,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: hours,
          boundaryGap: false,
          name: '小时后'
        },
        yAxis: {
          type: 'value',
          name: 'ROI',
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: series
      };
          
      this.compareChart.setOption(option);
    },
  },
  beforeDestroy() {
    if (this.trendChart) {
      this.trendChart.dispose();
    }
    if (this.compareChart) {
      this.compareChart.dispose();
    }
    // 移除resize监听
    window.removeEventListener('resize', this.calculateTableHeight);
  }
};
</script>

<style scoped>
.hourly-revenue-container {
  padding: 20px;
  min-height: calc(100vh - 60px);
  background: #f5f7fa;
}

.query-card, .list-card {
  margin-bottom: 20px;
  border-radius: 4px;
  border: none;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.card-header i {
  font-size: 16px;
  color: #409EFF;
}

/* 加载状态 */
.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #409EFF;
}

.loading-wrapper i {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-wrapper p {
  font-size: 14px;
  margin: 0;
}

/* 表格样式 - 简约风格 */
::v-deep .el-table {
  font-size: 13px;
  color: #606266;
  border: none;
}

::v-deep .el-table th {
  font-size: 13px;
  font-weight: 500;
  color: #909399;
  background: #fafafa;
  border-bottom: 1px solid #ebeef5;
  padding: 12px 0;
}

::v-deep .el-table td {
  font-size: 13px;
  padding: 10px 0;
  border-bottom: 1px solid #ebeef5;
}

::v-deep .el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: #f5f7fa;
}

::v-deep .el-table__row {
  background-color: #fff;
}

::v-deep .el-table__row.current-row > td {
  background-color: #ecf5ff;
}

/* 金额样式 */
.amount {
  font-size: 13px;
  color: #303133;
}

.amount.success { color: #67C23A; }
.amount.primary { color: #409EFF; }
.amount.warning { color: #E6A23C; }
.amount.info { color: #909399; }

/* ROI 样式 */
.roi-excellent { color: #67C23A; }
.roi-good { color: #409EFF; }
.roi-warning { color: #E6A23C; }
.roi-danger { color: #F56C6C; }

/* 按钮样式 */
::v-deep .el-button--text {
  font-size: 13px;
  padding: 0;
  color: #409EFF;
}

::v-deep .el-button--text:hover {
  color: #66b1ff;
}

/* 详情内容 */
.detail-content {
  padding: 10px;
}

.chart-section, .table-section {
  margin-top: 20px;
}

.chart-section h3, .table-section h3 {
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.chart-section h3 i, .table-section h3 i {
  color: #409EFF;
  font-size: 16px;
}
</style>
